// Visible domain normalization for Step 1 (DOMAIN_AUTHORIZATION_SPEC.md §3.3).

export const DOMAIN_ERRORS = {
  empty: "Enter your website's domain, like acme.com.",
  invalid: "That doesn't look like a domain. Try something like acme.com.",
  email: "That's an email address. Use just the part after @, like acme.com.",
} as const;

export const ORGANIZATION_ERRORS = {
  empty: "Enter your organization's name.",
  tooShort: 'Use at least 2 characters.',
  tooLong: 'Use 60 characters or fewer.',
} as const;

export type NormalizedDomain =
  | { ok: true; domain: string; changed: boolean; parent: string | null }
  | { ok: false; error: string };

const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const COMPOUND_SUFFIXES = ['co.uk', 'com.au', 'co.nz', 'co.jp', 'com.br'];

export function normalizeDomain(input: string): NormalizedDomain {
  const trimmed = input.trim();
  const raw = trimmed.toLowerCase();
  if (!raw) return { ok: false, error: DOMAIN_ERRORS.empty };
  if (raw.includes('@') && !raw.includes('://')) return { ok: false, error: DOMAIN_ERRORS.email };

  let host: string;
  try {
    host = new URL(raw.includes('://') ? raw : `https://${raw}`).hostname;
  } catch {
    return { ok: false, error: DOMAIN_ERRORS.invalid };
  }
  host = host.replace(/\.$/, '').replace(/^www\./, '');

  const labels = host.split('.');
  const valid =
    host.length <= 253 &&
    labels.length >= 2 &&
    labels.every((l) => LABEL.test(l)) &&
    /^[a-z]{2,63}$/.test(labels.at(-1) ?? '');
  if (!valid) return { ok: false, error: DOMAIN_ERRORS.invalid };

  const keep = COMPOUND_SUFFIXES.some((s) => host.endsWith(`.${s}`)) ? 3 : 2;
  const parent = labels.length > keep ? labels.slice(-keep).join('.') : null;
  return { ok: true, domain: host, changed: trimmed !== host, parent };
}

export function validateOrganization(input: string): string | null {
  const value = input.trim();
  if (!value) return ORGANIZATION_ERRORS.empty;
  if (value.length < 2) return ORGANIZATION_ERRORS.tooShort;
  if (value.length > 60) return ORGANIZATION_ERRORS.tooLong;
  return null;
}
